import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

function toTokens(session: CognitoUserSession): AuthTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
}

function getPool(): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
    ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
  });
}

// AdminCreateUser (the only way any portal_users row is ever created --
// onboarding, Team invites, the internal bootstrap script) always issues a
// temporary password and forces a change at first login. With the pool on
// mfa_configuration = "ON", completing that challenge yields ANOTHER
// challenge (MFA_SETUP) rather than tokens -- so every step of the chain
// resolves a LoginResult, and completeNewPassword cannot promise tokens.
export type LoginResult =
  | { type: "success"; tokens: AuthTokens }
  | { type: "new_password_required";
      completeNewPassword: (newPassword: string) => Promise<LoginResult> }
  | { type: "mfa_setup"; secretCode: string;
      verify: (code: string) => Promise<LoginResult> }
  | { type: "totp_required"; submit: (code: string) => Promise<LoginResult> };

export function loginWithPassword(email: string, password: string): Promise<LoginResult> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });

  return new Promise((resolve, reject) => {
    const toSuccess = (res: (r: LoginResult) => void) => (s: CognitoUserSession) =>
      res({ type: "success", tokens: toTokens(s) });

    // MFA_SETUP: ask Cognito for a fresh TOTP secret, then hand the caller a
    // verify() that redeems the first code from the authenticator app.
    function associate(res: (r: LoginResult) => void, rej: (e: unknown) => void) {
      user.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => res({
          type: "mfa_setup",
          secretCode,
          verify: (code: string) => new Promise<LoginResult>((res2, rej2) => {
            user.verifySoftwareToken(code, "portal", {
              onSuccess: toSuccess(res2),
              onFailure: rej2,
            });
          }),
        }),
        onFailure: rej,
      });
    }

    function totpChallenge(): LoginResult {
      return {
        type: "totp_required",
        submit: (code: string) => new Promise<LoginResult>((res, rej) => {
          // The third argument is required. Omitted, the SDK defaults to
          // SMS_MFA, which fails against a software-token-only pool.
          user.sendMFACode(code, { onSuccess: toSuccess(res), onFailure: rej },
                           "SOFTWARE_TOKEN_MFA");
        }),
      };
    }

    // One callback object, reused for the initial authenticateUser and for
    // completeNewPasswordChallenge: both can answer with any of the same
    // challenges, and duplicating the handlers is how one branch silently
    // loses a case.
    const callbacks = {
      onSuccess: toSuccess(resolve),
      onFailure: (err: unknown) => reject(err),
      mfaSetup: () => associate(resolve, reject),
      totpRequired: () => resolve(totpChallenge()),
      newPasswordRequired: () => resolve({
        type: "new_password_required",
        completeNewPassword: (newPassword: string) =>
          new Promise<LoginResult>((res, rej) => {
            user.completeNewPasswordChallenge(newPassword, {}, {
              onSuccess: toSuccess(res),
              onFailure: rej,
              mfaSetup: () => associate(res, rej),
              totpRequired: () => res(totpChallenge()),
            } as never);
          }),
      }),
    };

    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      callbacks as never,
    );
  });
}

export function refreshTokens(email: string, refreshToken: string): Promise<AuthTokens> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.refreshSession(new CognitoRefreshToken({ RefreshToken: refreshToken }), (err, session) => {
      if (err) return reject(err);
      resolve(toTokens(session as CognitoUserSession));
    });
  });
}

// Self-service reset needs no backend: Cognito's ForgotPassword is callable
// with the public SPA client. It works only for a user who has completed
// their first login (UserStatus CONFIRMED) -- a user still on a temporary
// password gets NotAuthorizedException, which LoginPage must translate into
// "ask your administrator", not a generic failure.
export function requestPasswordReset(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmPasswordReset(
  email: string, code: string, newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
